import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { getThreads, createThread as createThreadService, uploadThreadDocument, getThreadUploads } from '../apis/ThreadService'
import type { ThreadUpload } from '../apis/Types';
import {createTenant as createTenantService } from '../apis/TenantService';
import { getMessages, startNewConversation, sendMessage } from '../apis/MessageService'
import { fetchAllPolicies, uploadNewPolicy, fetchPolicyDetails, deletePolicyExtraction } from '../apis/PolicyService';
import { fetchUploadList, deleteUpload as deleteUploadService, 
  analyzePolicy, fetchPolicyAnalysis, uploadNewPolicies, fetchAnalysisDetails, downloadPolicyAnalysis } from '../apis/PolicyAnalysisService';
import { uploadFile, type UploadResponse } from '../apis/UploadService';
import { fetchUsersByTenant, fetchAllTenants, grantAdminAccess, revokeAdminAccess, deactivateUser, fetchAvailableUserPerTenant, addUserToTenant } from '../apis/AdminService';
import {
  fetchAllClientEnvironments,
  createClientEnvironment as createClientEnvironmentService,
  editClientEnvironment as editClientEnvironmentService,
  deleteClientEnvironment as deleteClientEnvironmentService,
} from '../apis/ClientEnvironmentService';
import { fetchConfigurationComparisons, fetchConfigurationUploads, fetchSasUrl, uploadFileToSasUrl, confirmSasUpload, uploadConfigurationFiles, fetchUploadsByEnvironment, fetchEnvironmentFiles, fetchZipStructure, bulkCompare, fetchComparisonDetail, fetchComparisonResultData, runComparison as runComparisonService, deleteConfigurationUpload as deleteConfigurationUploadService,deleteConfigurationComparison as deleteConfigurationComparisonService } from '../apis/ConfigurationComparisonService';
import { ConversationDTO, MessageDTO, PolicyExtractionListResponse, PolicyDetailsDTO, PolicyUploadListResponse, PolicyComparisonListResponse, PolicyAnalysisDTO, UserDetailsListResponse, TenantListResponse, UserDetails, ConfigurationComparisonListResponse, ConfigurationComparisonDTO, ComparisonResultData, ConfigurationUploadListResponse, ClientEnvironmentListResponse, ClientEnvironmentDTO, EnvironmentFileDTO, EnvironmentUploadDTO, BulkComparePayload, SasUrlResponse, ConfirmSasUploadPayload, ConfirmSasUploadResponse, ZipStructureResponse } from './models';


export const api = axios.create({ baseURL: '/api' });

// Mock mode toggle (frontend-only) – if backend not running we short-circuit calls.
// Enable by setting VITE_MOCK_MODE=true in .env (vite env) or window.__MOCK_MODE__ at runtime.
const MOCK_MODE = (import.meta as any).env?.VITE_MOCK_MODE === 'true' || (window as any).__MOCK_MODE__ === true;

// In-memory mock stores
let mockConversations: ConversationDTO[] = [];

export type FetchPageOptions = {
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
};

export async function postChat(content: string) {
  const res = await api.post('/chat', { content });
  return res.data as { response: string };
}

export async function callOpenAI(prompt: string) {
  if (MOCK_MODE) {
    return Promise.resolve({ answer: `Mock AI response to: ${prompt}` });
  }
  const res = await api.post('/openai', { prompt });
  return res.data as { answer: string };
}

// Conversation persistence endpoints
export async function listConversations(params: { tenantId: string; offset?: number; limit?: number; q?: string; includeArchived?: boolean }) {
  const { tenantId, offset = 0, limit = 20 } = params;
  const threads = await getThreads(tenantId);
  if (import.meta.env.DEV) console.log('Fetched threads for tenant', tenantId);
  
  // Map threads to conversations
  const mappedConversations: ConversationDTO[] = threads.map(t => ({
    id: t.threadId,
    title: t.title,
    createdAt: t.createdAt || new Date().toISOString(),
    updatedAt: t.updatedAt || new Date().toISOString(),
    messages: [],
    archived: false,
    retryAttempts: 0
  }));
  return { conversations: mappedConversations, total: mappedConversations.length, offset, limit };
}

export async function createConversation(title?: string) {
  if (MOCK_MODE) {
    const now = new Date().toISOString();
    const convo: ConversationDTO = { id: uuid(), title: title || 'New Conversation', createdAt: now, updatedAt: now, messages: [], archived: false, retryAttempts: 0 };
    mockConversations.push(convo);
    return convo;
  }
  const res = await api.post('/conversations', { title });
  return res.data as ConversationDTO;
}

export async function getConversation(tenantId: string, threadId: string, signal?: AbortSignal): Promise<ConversationDTO> {
  
  const messages = await getMessages(tenantId, threadId, signal);
  if (import.meta.env.DEV) console.log('Fetched messages for thread', threadId);
  
  return {
    id: threadId,
    title: `Conversation for Thread ${threadId}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    messages: messages.map(m => ({
      id: m.id,
      role: m.role === 'user' ? 'user' : 'system',
      content: m.content,
      createdAt: new Date().toISOString()
    })),
    archived: false,
    retryAttempts: 0
  } as ConversationDTO;
}

export async function sendNewConversationMessage(tenantId: string, content: string): Promise<{ threadId: string }> {
  const response = await startNewConversation(tenantId, content);
  return response;
}

export async function createThreadForConversation(tenantId: string, signal?: AbortSignal): Promise<string> {
  return createThreadService(tenantId, signal);
}

export async function uploadThreadFile(tenantId: string, threadId: string, file: File, signal?: AbortSignal): Promise<ThreadUpload> {
  return uploadThreadDocument(tenantId, threadId, file, signal);
}

export async function listThreadFiles(tenantId: string, threadId: string, signal?: AbortSignal): Promise<ThreadUpload[]> {
  return getThreadUploads(tenantId, threadId, signal);
}

export async function sendConversationMessage(tenantId: string, conversationId: string, role: 'user' | 'system', content: string) {
  await sendMessage(tenantId, conversationId, content);
  const msg: MessageDTO = { id: uuid(), role, content, createdAt: new Date().toISOString() };
  return msg;
}

export async function patchConversation(conversationId: string, data: Partial<Pick<ConversationDTO, 'title' | 'archived' | 'retryAttempts'>>) {
  if (MOCK_MODE) {
    const convo = mockConversations.find(c => c.id === conversationId);
    if (!convo) throw new Error('Conversation not found');
    if (typeof data.title === 'string' && data.title.trim()) convo.title = data.title.trim();
    if (typeof data.archived === 'boolean') convo.archived = data.archived;
    if (typeof data.retryAttempts === 'number') convo.retryAttempts = data.retryAttempts;
    convo.updatedAt = new Date().toISOString();
    return convo;
  }
  const res = await api.patch(`/conversations/${conversationId}`, data);
  return res.data as ConversationDTO;
}

/**
 * @deprecated Use `deleteThread(tenantId, threadId)` from `../apis/ThreadService` instead.
 * This function calls the unauthenticated legacy axios instance and the `/api/conversations/:id`
 * path is no longer backed by a real API endpoint. Kept for mock-mode compatibility only.
 */
export async function archiveConversation(conversationId: string) {
  if (MOCK_MODE) {
    const convo = mockConversations.find(c => c.id === conversationId);
    if (convo) { convo.archived = true; convo.updatedAt = new Date().toISOString(); }
    return;
  }
  await api.delete(`/conversations/${conversationId}`);
}

// Placeholder search method to satisfy tests expecting a search API
export async function searchDocuments(query: string) {
  // In real implementation, call: await api.get(`/search`, { params: { q: query } })
  return { results: [], query } as { results: any[]; query: string };
}

export async function getAllPolicies(tenantId: string, options?: FetchPageOptions): Promise<PolicyExtractionListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchAllPolicies(tenantId, offset, limit, signal);
  return response;
}

export async function uploadPolicyExtraction(tenantId: string, location: string, file: File[], signal?: AbortSignal): Promise<void> {
  const response = await uploadNewPolicy(tenantId, location, file, signal);
  return response;
}

export async function getPolicyDetails(tenantId: string, policyExtractionId: string, signal?: AbortSignal): Promise<PolicyDetailsDTO> {
  const response = await fetchPolicyDetails(tenantId, policyExtractionId, signal);
  return response;
}

export async function deletePolicy(tenantId: string, policyExtractionId: string, signal?: AbortSignal): Promise<void> {
  await deletePolicyExtraction(tenantId, policyExtractionId, signal);
}

export type UploadFilesResult = {
  statusMessage: string;
  response: UploadResponse;
};

export async function uploadFiles(tenantId: string, file: File, signal?: AbortSignal): Promise<UploadFilesResult> {
  const response = await uploadFile(tenantId, file, signal);

  if (!response) {
    throw new Error(`${file.name}: Upload failed.`);
  }

  if (response.success) {
    const blobName = response.blobName;
    const size = typeof response.size === 'number' ? ` (${Math.round(response.size / 1024)}KB)` : '';
    const url = response.blobUrl ? `\nSAS (5m): ${response.blobUrl}` : '';
    const etag = response.eTag ? `\nETag: ${response.eTag}` : '';
    const hash = response.contentHashMd5 ? `\nMD5: ${response.contentHashMd5}` : '';
    const msg = response.message ? `\nMsg: ${response.message}` : '';
    const retryMeta = response.retry ? `\nRetry: ${JSON.stringify(response.retry)}` : '';
    const statusMessage = `${file.name} uploaded${blobName ? ' → ' + blobName : ''}${size}${url}${etag}${hash}${msg}${retryMeta}`;

    return { statusMessage, response };
  }

  const err = response.error ? `[${response.error}] ` : '';
  const detail = response.detail ? `: ${response.detail}` : '';
  const fallbackMessage = response.message || 'Upload failed';
  throw new Error(`${file.name}: ${err}${fallbackMessage}${detail}`);
}

export async function getUploadList(tenantId: string, options?: FetchPageOptions): Promise<PolicyUploadListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchUploadList(tenantId, offset, limit, signal);
  return response;
}

export async function deleteUpload(tenantId: string, configurationId: string, signal?: AbortSignal): Promise<void> {
  await deleteUploadService(tenantId, configurationId, signal);
}

export async function createPolicyAnalysis(tenantId: string, payload: { configurationType: number; configurationSubtype: string; continent: string; country: string; state: string }, signal?: AbortSignal): Promise<void> {
  await analyzePolicy(tenantId, payload, signal);
}

export async function getPolicyAnalysis(tenantId: string, options?: FetchPageOptions): Promise<PolicyComparisonListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchPolicyAnalysis(tenantId, offset, limit, signal);
  return response;
}

export async function uploadPolicy(tenantId: string, configurationType : number, file: File[], signal?: AbortSignal): Promise<void> {
  const response = await uploadNewPolicies(tenantId, configurationType, file, signal);
  return response;
}

export async function getAnalysisDetails(tenantId: string, comparisonId: string, signal?: AbortSignal): Promise<PolicyAnalysisDTO> {
  const response = await fetchAnalysisDetails(tenantId, comparisonId, signal);
  return response;
}

export async function policyAnalysisDownload(downloadUrl: string, fileName: string, signal?: AbortSignal): Promise<void> {
  const blob = await downloadPolicyAnalysis(downloadUrl, signal);
  const blobUrl = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = blobUrl;
	link.download = fileName && fileName.trim() ? fileName : 'policy-comparison.csv';
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(blobUrl);
}

export async function getUsersByTenant(tenantId: string, options?: FetchPageOptions): Promise<UserDetailsListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchUsersByTenant(tenantId, offset, limit, signal);
  return response;
}

export async function getAllTenants(options?: FetchPageOptions): Promise<TenantListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchAllTenants(offset, limit, signal);
  return response;
}

export async function createTenant(payload: { name: string; description: string }, signal?: AbortSignal): Promise<{id: string}> {
  const response = await createTenantService(payload, signal);
  return response;
}

export async function addAdminAccess(userId: string, signal?: AbortSignal): Promise<UserDetails> {
  const response = await grantAdminAccess(userId, signal);
  return response;
}

export async function removeAdminAccess(userId: string, signal?: AbortSignal): Promise<UserDetails> {
  const response = await revokeAdminAccess(userId, signal);
  return response;
}

export async function removeUserAccess(tenantId: string, userId: string, signal?: AbortSignal): Promise<void> {
  await deactivateUser(tenantId, userId, signal);
}

export async function getAvailableUsersByTenant(tenantId: string, signal?: AbortSignal): Promise<UserDetails[]> {
  const response = await fetchAvailableUserPerTenant(tenantId, signal);
  return response;
}

export async function addUsersToTenant(tenantId: string, userIds: string[], signal?: AbortSignal): Promise<void> {
  await addUserToTenant(tenantId, userIds, signal);
}

// Configuration Comparison
export async function getAllConfigurationComparisons(tenantId: string, options?: FetchPageOptions): Promise<ConfigurationComparisonListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchConfigurationComparisons(tenantId, offset, limit, signal);
  return response;
}

export async function getAllConfigurationUploads(tenantId: string, options?: FetchPageOptions): Promise<ConfigurationUploadListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchConfigurationUploads(tenantId, offset, limit, signal);
  return response;
}
export async function deleteConfigurationUpload(tenantId: string,uploadId: string,signal?: AbortSignal): Promise<void> {
  await deleteConfigurationUploadService(tenantId, uploadId, signal); 
}

export async function deleteConfigurationComparison(tenantId: string, comparisonId: string, signal?: AbortSignal): Promise<void> {
  await deleteConfigurationComparisonService(tenantId, comparisonId, signal);
}
/**
 * @deprecated Use {@link uploadConfigurationFile} instead.
 */
export async function getSasUrl(tenantId: string, fileName: string, signal?: AbortSignal): Promise<SasUrlResponse> {
  return fetchSasUrl(tenantId, fileName, signal);
}

/**
 * @deprecated Use {@link uploadConfigurationFile} instead.
 */
export async function uploadToSasUrl(sasUrl: string, file: File, signal?: AbortSignal): Promise<void> {
  await uploadFileToSasUrl(sasUrl, file, signal);
}

/**
 * @deprecated Use {@link uploadConfigurationFile} instead.
 */
export async function confirmSasUploadDetails(tenantId: string, payload: ConfirmSasUploadPayload, signal?: AbortSignal): Promise<ConfirmSasUploadResponse> {
  return confirmSasUpload(tenantId, payload, signal);
}

export async function uploadConfigurationFile(
  tenantId: string,
  environmentId: string,
  name: string,
  description: string,
  files: File[],
  signal?: AbortSignal,
): Promise<void> {
  await uploadConfigurationFiles(tenantId, environmentId, name, description, files, signal);
}

// Client Environment
export async function getAllClientEnvironments(tenantId: string, options?: FetchPageOptions): Promise<ClientEnvironmentListResponse> {
  const { offset = 0, limit = 10, signal } = options ?? {};
  const response = await fetchAllClientEnvironments(tenantId, offset, limit, signal);
  return response;
}

export async function createClientEnvironment(tenantId: string, payload: { name: string; description: string }, signal?: AbortSignal): Promise<ClientEnvironmentDTO> {
  const response = await createClientEnvironmentService(tenantId, payload, signal);
  return response;
}

export async function editClientEnvironment(tenantId: string, environmentId: string, payload: { name: string; description: string }, signal?: AbortSignal): Promise<ClientEnvironmentDTO> {
  const response = await editClientEnvironmentService(tenantId, environmentId, payload, signal);
  return response;
}

export async function removeClientEnvironment(tenantId: string, environmentId: string, signal?: AbortSignal): Promise<void> {
  await deleteClientEnvironmentService(tenantId, environmentId, signal);
}

// Environment Comparison
export async function getUploadsByEnvironment(tenantId: string, environmentId: string, signal?: AbortSignal): Promise<EnvironmentUploadDTO[]> {
  const response = await fetchUploadsByEnvironment(tenantId, environmentId, signal);
  return response;
}

export async function getEnvironmentFiles(tenantId: string, environmentId: string, signal?: AbortSignal): Promise<EnvironmentFileDTO[]> {
  const response = await fetchEnvironmentFiles(tenantId, environmentId, signal);
  return response;
}

export async function getZipStructure(tenantId: string, environmentId: string, zipBlobName: string, signal?: AbortSignal): Promise<ZipStructureResponse> {
  const response = await fetchZipStructure(tenantId, environmentId, zipBlobName, signal);
  return response;
}

export async function createBulkCompare(tenantId: string, payload: BulkComparePayload, signal?: AbortSignal): Promise<void> {
  await bulkCompare(tenantId, payload, signal);
}

export async function getComparisonDetail(tenantId: string, comparisonId: string, signal?: AbortSignal): Promise<ConfigurationComparisonDTO> {
  return fetchComparisonDetail(tenantId, comparisonId, signal);
}

export async function getComparisonResultData(tenantId: string, comparisonId: string, signal?: AbortSignal): Promise<ComparisonResultData | null> {
  return fetchComparisonResultData(tenantId, comparisonId, signal);
}

export async function rerunComparison(tenantId: string, comparisonId: string, signal?: AbortSignal): Promise<void> {
  await runComparisonService(tenantId, comparisonId, signal);
}